"use client"

import { convertToLocale } from "@lib/util/money"
import { CheckCircleSolid, XMark } from "@medusajs/icons"
import {
  HttpTypes,
  StoreCart,
  StoreCartShippingOption,
  StorePrice,
} from "@medusajs/types"
import { Button, clx } from "@medusajs/ui"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useState } from "react"
import { StoreFreeShippingPrice } from "types/global"

const computeTarget = (
  cart: HttpTypes.StoreCart,
  price: HttpTypes.StorePrice
) => {
  const priceRule = (price.price_rules || []).find(
    (pr) => pr.attribute === "item_total"
  )!

  const currentAmount =
  cart.original_item_total ??
  cart.subtotal ??
  cart.item_total
  const targetAmount = parseFloat(priceRule.value)

  if (priceRule.operator === "gt") {
    return {
      current_amount: currentAmount,
      target_amount: targetAmount,
      target_reached: currentAmount > targetAmount,
      target_remaining:
        currentAmount > targetAmount ? 0 : targetAmount + 1 - currentAmount,
      remaining_percentage: (currentAmount / targetAmount) * 100,
    }
  } else if (priceRule.operator === "gte") {
    return {
      current_amount: currentAmount,
      target_amount: targetAmount,
      target_reached: currentAmount >= targetAmount,
      target_remaining:
        currentAmount >= targetAmount ? 0 : targetAmount - currentAmount,
      remaining_percentage: (currentAmount / targetAmount) * 100,
    }
  } else if (priceRule.operator === "lt") {
    return {
      current_amount: currentAmount,
      target_amount: targetAmount,
      target_reached: targetAmount > currentAmount,
      target_remaining:
        targetAmount > currentAmount ? 0 : currentAmount + 1 - targetAmount,
      remaining_percentage: (currentAmount / targetAmount) * 100,
    }
  } else if (priceRule.operator === "lte") {
    return {
      current_amount: currentAmount,
      target_amount: targetAmount,
      target_reached: targetAmount >= currentAmount,
      target_remaining:
        targetAmount >= currentAmount ? 0 : currentAmount - targetAmount,
      remaining_percentage: (currentAmount / targetAmount) * 100,
    }
  } else {
    return {
      current_amount: currentAmount,
      target_amount: targetAmount,
      target_reached: currentAmount === targetAmount,
      target_remaining:
        currentAmount >= targetAmount ? 0 : targetAmount - currentAmount,
      remaining_percentage: (currentAmount / targetAmount) * 100,
    }
  }
}

export default function ShippingPriceNudge({
  variant = "inline",
  cart,
  shippingOptions,
}: {
  variant?: "popup" | "inline"
  cart: StoreCart
  shippingOptions: StoreCartShippingOption[]
}) {
  if (!cart || !shippingOptions?.length) {
    return
  }

  const freeShippingPrice = shippingOptions
    .map((shippingOption) => {
      const calculatedPrice = shippingOption.calculated_price

      if (!calculatedPrice) {
        return
      }

      const validCurrencyPrices = shippingOption.prices.filter(
        (price) =>
          price.currency_code === cart.currency_code &&
          (price.price_rules || []).some(
            (priceRule) => priceRule.attribute === "item_total"
          )
      )

      return validCurrencyPrices.map((price) => {
        return {
          ...price,
          shipping_option_id: shippingOption.id,
          ...computeTarget(cart, price),
        }
      })
    })
    .flat(1)
    .filter(Boolean)
    .find((price) => price?.amount === 0)

  if (!freeShippingPrice) {
    return
  }

  return <FreeShippingInline cart={cart} price={freeShippingPrice} />
}

function FreeShippingInline({
  cart,
  price,
}: {
  cart: StoreCart
  price: StorePrice & {
    target_reached: boolean
    target_remaining: number
    target_amount: number
    remaining_percentage: number
  }
}) {
  const progress = Math.max(0, Math.min(price.remaining_percentage, 100))

  return (
    <div
      className="relative rounded-2xl border border-white/30 px-4 py-3 shadow-[0_10px_28px_rgba(0,0,0,0.45)] backdrop-blur-[2px]"
      style={{
        background:
          "linear-gradient(90deg, #0b1a2f 0%, #0f2a4d 45%, #1f2f46 100%)",
      }}
    >
      <div className="space-y-2.5">
        <div className="text-[11px] font-bold tracking-[0.04em] text-white/80 uppercase">
          Free shipping from{" "}
          <span className="text-white">
            {convertToLocale({
              amount: price.target_amount,
              currency_code: cart.currency_code,
            })}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3 text-[13px] font-semibold text-white">
          <div className="leading-none">
            {price.target_reached ? (
              <div className="flex items-center gap-1.5 font-extrabold text-white">
                <CheckCircleSolid className="inline-block text-green-400" />
                Free Shipping unlocked!
              </div>
            ) : (
              <span className="font-extrabold tracking-[0.01em] text-white">
                Unlock Free Shipping
              </span>
            )}
          </div>

          <div
            className={clx("text-right text-white/95", {
              "opacity-0 invisible": price.target_reached,
            })}
          >
            Only{" "}
            <span className="font-extrabold text-white">
              {convertToLocale({
                amount: price.target_remaining,
                currency_code: cart.currency_code,
              })}
            </span>{" "}
            away
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <div className="h-[5px] flex-grow overflow-hidden rounded-full bg-white/12">
            <div
              className="h-full rounded-full transition-all duration-500 ease-in-out"
              style={{
                width: `${progress}%`,
                background: price.target_reached
                  ? "#22c55e"
                  : "linear-gradient(90deg, #f97316 0%, #fb923c 32%, #f59e0b 52%, #84cc16 76%, #22c55e 100%)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function FreeShippingPopup({
  cart,
  price,
}: {
  cart: StoreCart
  price: StoreFreeShippingPrice
}) {
  const [isClosed, setIsClosed] = useState(false)

  return (
    <div
      className={clx(
        "fixed bottom-5 right-5 z-10 flex flex-col items-end gap-2 transition-all duration-500 ease-in-out",
        {
          "opacity-0 invisible delay-1000": price.target_reached,
          "opacity-0 invisible": isClosed,
          "opacity-100 visible": !price.target_reached && !isClosed,
        }
      )}
    >
      <div>
        <Button
          className="rounded-full border-none bg-neutral-900 p-2 text-[15px] shadow-none outline-none"
          onClick={() => setIsClosed(true)}
        >
          <XMark />
        </Button>
      </div>

      <div className="w-[400px] rounded-lg bg-black p-6 text-white">
        <div className="pb-4">
          <div className="space-y-3">
            <div className="flex justify-between text-[15px] text-neutral-400">
              <div>
                {price.target_reached ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircleSolid className="inline-block text-green-500" />
                    Free Shipping unlocked!
                  </div>
                ) : (
                  `Unlock Free Shipping`
                )}
              </div>

              <div
                className={clx("visible", {
                  "opacity-0 invisible": price.target_reached,
                })}
              >
                Only{" "}
                <span className="text-white">
                  {convertToLocale({
                    amount: price.target_remaining,
                    currency_code: cart.currency_code,
                  })}
                </span>{" "}
                away
              </div>
            </div>

            <div className="flex justify-between gap-1">
              <div
                className={clx(
                  "max-w-full rounded-full h-1.5 bg-gradient-to-r from-zinc-400 to-zinc-500 duration-500 ease-in-out",
                  {
                    "from-green-400 to-green-500": price.target_reached,
                  }
                )}
                style={{ width: `${price.remaining_percentage}%` }}
              />
              <div className="h-1.5 w-fit flex-grow rounded-full bg-zinc-600" />
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <LocalizedClientLink
            className="rounded-2xl border-[1px] border-white bg-transparent px-4 py-2.5 text-[15px] shadow-none outline-none"
            href="/cart"
          >
            View cart
          </LocalizedClientLink>

          <LocalizedClientLink
            className="flex-grow rounded-2xl border-[1px] border-white bg-white px-4 py-2.5 text-center text-[15px] text-neutral-950 shadow-none outline-none"
            href="/store"
          >
            View products
          </LocalizedClientLink>
        </div>
      </div>
    </div>
  )
}